import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import produce from 'utils/produce';
import { isEmailValid } from 'utils/validator';
import { Form } from 'components/base';
import useDataContext from 'data/context/data';

const VALIDATOR = (address, { requireContact = true } = {}) => {
  const NAME = () => !address.name || address.name.split(" ").filter(str => str.length > 0).length < 2;
  const STREET = () => !address.street || address.street.length < 2;
  const ZIP = () => !address.zip || address.zip.length < 4;
  const CITY = () => !address.city || address.city.length < 2;
  const STATE = () =>
    address.countryCode &&
    ['AU', 'CA', 'US', 'NZ', 'IN'].indexOf(address.countryCode) >= 0 &&
    (!address.state || address.state.length < 2);
  const PHONE = () => !address.phone || address.phone.length < 5;
  const EMAIL = () => !address.email || !isEmailValid(address.email);

  // Billing addresses reuse this form but don't collect email/phone, so those
  // checks are skipped when contact details aren't required.
  const checks = { name: NAME, street: STREET, zip: ZIP, city: CITY, state: STATE };
  if (requireContact) {
    Object.assign(checks, { phone: PHONE, email: EMAIL });
  }

  return {
    NAME,
    STREET,
    ZIP,
    CITY,
    STATE,
    PHONE,
    EMAIL,
    IS_VALID: () => Object.values(checks).every(fn => Boolean(!fn())),
    // checkout_field_error params: an empty field is required, a filled one has the wrong format.
    ERRORS: () => Object.entries(checks)
      .filter(([, fn]) => fn())
      .map(([field]) => ({ field, type: address[field] ? 'invalid_format' : 'required' })),
  };
};

const CheckoutShippingAddress = props => {
  const isEditable = useMemo(() => props.onChange != null, [props.onChange]);
  const dataContext = useDataContext();

  /**
   * Due to changing address externally, it's necessary to match country.name.
   * Also the issue is, that sometimes user has defined alpha3 instead of alpha2.
   *
   * TODO dont use DataContext, but use own context for shipping countries
   *
   * @param {Object} address
   */
  const assignCountryToShippingAddress = address => {
    if (address.countryCode) {
      const country = dataContext.countries.find(
        item => item.alpha2 === address.countryCode || item.alpha3 === address.countryCode,
      );
      if (country) {
        return {
          ...address,
          country: country.name,
          countryCode: country.alpha2,
        };
      }
    }

    return address;
  };

  const shippingAddress = assignCountryToShippingAddress(props.shippingAddress);

  useEffect(() => {
    const addressValidator = VALIDATOR(shippingAddress, { requireContact: props.requireContact });
    if (addressValidator.IS_VALID()) {
      props.innerRef.current = {
        isValid: true,
        fieldErrors: () => [],
        getShippingAddress: () => {
          return {
            name: shippingAddress.name,
            street: shippingAddress.street,
            street2: shippingAddress.street2 || '',
            zip: shippingAddress.zip,
            city: shippingAddress.city,
            state: shippingAddress.state || '',
            phone: shippingAddress.phone,
            email: shippingAddress.email,
            country: shippingAddress.country,
            country_code: shippingAddress.countryCode,
          };
        },
      };
    } else {
      const errors = addressValidator.ERRORS();
      props.innerRef.current = {
        isValid: false,
        fieldErrors: () => errors,
      };
    }
  }, [shippingAddress, props.innerRef, props.requireContact]);

  const handleOnChangeCountry = e => {
    const country = dataContext.countries.find(item => item.alpha2 === e.target.value);

    if (country) {
      props.onChange(
        produce(shippingAddress, draft => {
          draft.country = country.name;
          draft.countryCode = country.alpha2;
        }),
      );
    }
  };

  const handleOnChangeValue = (e, key) => {
    props.onChange(
      produce(shippingAddress, draft => {
        draft[key] = e.target.value;
      }),
    );
  };

  const validator = VALIDATOR(shippingAddress, { requireContact: props.requireContact });

  return (
    <>
      <Form.Group>
        <Form.Label>Country</Form.Label>
        {isEditable ? (
          <Form.Select value={shippingAddress.countryCode} onChange={handleOnChangeCountry}>
            {dataContext.countries.map(country => (
              <Form.SelectOption key={country.alpha2} value={country.alpha2}>
                {country.name}
              </Form.SelectOption>
            ))}
          </Form.Select>
        ) : (
          <Form.Input
            type="text"
            disabled
            value={shippingAddress.country ?? shippingAddress.countryCode}
          />
        )}
      </Form.Group>
      <Form.Group>
        <Form.Label className="d-flex justify-content-between">
          <span>Full Name</span>
          {props.isValidating && validator.NAME() && (
            <span className="text-danger">Please enter full name</span>
          )}
        </Form.Label>
        <Form.Input
          type="text"
          value={shippingAddress.name}
          onChange={e => handleOnChangeValue(e, 'name')}
          disabled={!isEditable}
          {...(props.isValidating && { isInvalid: validator.NAME() })}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>Street Address</Form.Label>
        <Form.Input
          type="text"
          value={shippingAddress.street}
          onChange={e => handleOnChangeValue(e, 'street')}
          disabled={!isEditable}
          {...(props.isValidating && { isInvalid: validator.STREET() })}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>Street Address 2</Form.Label>
        <Form.Input
          type="text"
          value={shippingAddress.street2}
          onChange={e => handleOnChangeValue(e, 'street2')}
          disabled={!isEditable}
        />
      </Form.Group>
      <Form.Row>
        <Form.Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>City</Form.Label>
            <Form.Input
              type="text"
              value={shippingAddress.city}
              onChange={e => handleOnChangeValue(e, 'city')}
              disabled={!isEditable}
              {...(props.isValidating && { isInvalid: validator.CITY() })}
            />
          </Form.Group>
        </Form.Col>
        <Form.Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>State</Form.Label>
            <Form.Input
              type="text"
              value={shippingAddress.state}
              onChange={e => handleOnChangeValue(e, 'state')}
              disabled={!isEditable}
              {...(props.isValidating && { isInvalid: validator.STATE() })}
            />
          </Form.Group>
        </Form.Col>
        <Form.Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>ZIP</Form.Label>
            <Form.Input
              type="text"
              value={shippingAddress.zip}
              onChange={e => handleOnChangeValue(e, 'zip')}
              disabled={!isEditable}
              {...(props.isValidating && { isInvalid: validator.ZIP() })}
            />
          </Form.Group>
        </Form.Col>
      </Form.Row>
      {props.requireContact && (
        <>
          <Form.Group>
            <Form.Label>Email</Form.Label>
            <Form.Input
              type="text"
              value={shippingAddress.email}
              onChange={e => handleOnChangeValue(e, 'email')}
              disabled={!isEditable}
              {...(props.isValidating && { isInvalid: validator.EMAIL() })}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Phone</Form.Label>
            <Form.Input
              type="text"
              value={shippingAddress.phone}
              onChange={e => handleOnChangeValue(e, 'phone')}
              disabled={!isEditable}
              {...(props.isValidating && { isInvalid: validator.PHONE() })}
            />
          </Form.Group>
        </>
      )}
    </>
  );
};

CheckoutShippingAddress.propTypes = {
  innerRef: PropTypes.object,
  shippingAddress: PropTypes.object,
  isValidating: PropTypes.bool,
  requireContact: PropTypes.bool,
  onChange: PropTypes.func,
};

CheckoutShippingAddress.defaultProps = {
  shippingAddress: {},
  isValidating: false,
  requireContact: true,
};

export default CheckoutShippingAddress;
